import { useParams } from 'react-router'
import { DragAndDroppedShareIds } from './useShareDragAndDrop'
import {
  readStorageItems,
  removeFromStorageItemsList,
  RouteParams,
} from '@sketch/modules-common'
import { useCreateProject } from '../../projects/hooks/useCreateProject'
import {
  GetStorageItemsDocument,
  GetStorageItemsQueryVariables,
  useGetProjectDetailsQuery,
} from '@sketch/gql-types'
import { useGenerateFolderName } from './useGenerateFolderName'
import produce from 'immer'

interface UseDropShareToNestedProjectProps {
  projectIdentifier?: string
  storageItemsQueryVariables: GetStorageItemsQueryVariables
}

export const useDropShareToNestedProject = ({
  projectIdentifier,
  storageItemsQueryVariables,
}: UseDropShareToNestedProjectProps) => {
  const { workspaceId } =
    useParams<RouteParams<'WORKSPACE_PROJECT' | 'WORKSPACE_DRAFTS'>>()

  // We need the project info for the optimistic response, this data it's already in cache
  const { data: currentProject } = useGetProjectDetailsQuery({
    variables: { projectIdentifier: projectIdentifier || '' },
    skip: !projectIdentifier,
  })

  const { createProject } = useCreateProject()

  const generateFolderName = useGenerateFolderName(
    storageItemsQueryVariables,
    projectIdentifier
  )

  const newFolderName = generateFolderName()

  // Creates a nested project with the dragged share and the dropped share
  const dropShareToNestedProject = async ({
    draggedShareId,
    droppedShareId,
  }: DragAndDroppedShareIds) => {
    // we need a valid project id to create a nested project
    if (!projectIdentifier) {
      return
    }

    if (draggedShareId === droppedShareId) {
      // If share was dropped on itself, ignore
      return
    }

    // Create nested project with both shares inside
    createProject({
      variables: {
        workspaceId,
        parentProjectIdentifier: projectIdentifier,
        name: newFolderName,
        description: '',
        storageItems: [
          { identifier: draggedShareId, type: 'SHARE' },
          { identifier: droppedShareId, type: 'SHARE' },
        ],
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        createWorkspaceProject: {
          __typename: 'CreateWorkspaceProjectResponse',
          project: {
            __typename: 'Project',
            archivedAt: null,
            deletedAt: null,
            description: null,
            userCanLeave: false,
            projectPrivacyIcon: 'WORKSPACE_MEMBERS',
            identifier: 'new-project-id',
            name: newFolderName,
            parentProjectIdentifier: projectIdentifier,
            pinnedByCurrentUserAt: null,
            projectType: 'STANDARD',
            parentProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: currentProject?.project?.name ?? '',
              nestedProjectsCount:
                (currentProject?.project?.nestedProjectsCount ?? 0) + 1,
              sharesCount: (currentProject?.project?.sharesCount ?? 0) - 2,
            },
            topLevelProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: '',
              workspaceAccessLevel:
                currentProject?.project?.workspaceAccessLevel ?? 'NONE',
            },
            workspaceAccessLevel:
              currentProject?.project?.workspaceAccessLevel ?? 'NONE',
          },
        },
      },
      update: (cache, { data }) => {
        // The strategy is to create the new project as quickly as possible to
        // give a sense of speed (that's possible because we are using an
        // optimistic response), and after the mutation is completed, the
        // nested project will be filled with the real data.
        const storageItems = readStorageItems(cache, storageItemsQueryVariables)

        const newProject = data?.createWorkspaceProject.project

        const newStorageItems = produce(storageItems, draft => {
          if (
            !draft?.project?.storageItems ||
            !newProject ||
            newProject.__typename !== 'Project'
          ) {
            return
          }

          draft.project.storageItems.meta.nestedProjectsCount += 1
          draft.project.storageItems.meta.totalCount += 1

          draft.project.storageItems.entries.unshift({
            ...newProject,
            sharesCount: 2,
            nestedProjectsCount: 0,
            previews: [],
          })

          draft.project.storageItems.entries.sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        })

        cache.writeQuery({
          query: GetStorageItemsDocument,
          variables: storageItemsQueryVariables,
          data: newStorageItems,
        })

        // We also want to remove the shares from the parent project
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Share',
            identifier: draggedShareId,
          },
        })
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Share',
            identifier: droppedShareId,
          },
        })
      },
    })
  }

  return dropShareToNestedProject
}
